import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import pocketbaseService from '../services/pocketbase';
import CollectionView from './CollectionView';

const { FiFolder, FiPlus, FiDatabase, FiRefreshCw, FiLayers, FiBox, FiTrash2, FiLink } = FiIcons;

const ProjectDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCol, setSelectedCol] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      console.log("Iniciando carga de Workspace...");
      // Forçamos a busca separada para evitar que uma falha cancele a outra
      const projs = await pocketbaseService.getGretaProjects();
      const allCols = await pocketbaseService.getCollections();
      
      setProjects(projs || []);
      setCollections(allCols || []);
      console.log("Workspace sincronizado com sucesso.");
    } catch (err) {
      console.error("Erro no Workspace:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateProject = async () => {
    const name = prompt("Nome da Pasta:");
    if (!name) return;

    try {
      setLoading(true);
      await pocketbaseService.saveGretaProject({
        name,
        collections_list: [],
        description: 'Contexto Greta'
      });
      // Delay estratégico para persistência no PocketBase
      setTimeout(() => loadData(), 500);
    } catch (e) {
      alert("Erro ao salvar pasta: " + e.message);
      setLoading(false);
    }
  };

  if (selectedCol) return <CollectionView collection={selectedCol} onBack={() => setSelectedCol(null)} />;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex justify-between items-center mb-12">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tighter flex items-center italic">
            <SafeIcon icon={FiLayers} className="mr-3 text-indigo-600" /> Workspace
          </h1>
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Status: Sincronizado</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleCreateProject}
            disabled={loading}
            className="bg-slate-900 text-white px-6 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl flex items-center space-x-2 disabled:opacity-50"
          >
            <SafeIcon icon={FiPlus} />
            <span>Nova Pasta</span>
          </button>
          <button onClick={loadData} className="p-4 bg-white border border-slate-100 rounded-2xl shadow-sm">
            <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="grid gap-12">
        <section>
          <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6">Pastas de Contexto ({projects.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.length === 0 && !loading ? (
              <div className="col-span-full py-16 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[3rem] text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Nenhum dado encontrado em greta_projects</p>
              </div>
            ) : projects.map(proj => (
              <div key={proj.id} className="bg-white border border-slate-100 p-8 rounded-[2.5rem] shadow-sm hover:shadow-xl transition-all group relative">
                <button 
                  onClick={async () => { if(confirm('Remover pasta?')) { await pocketbaseService.deleteGretaProject(proj.id); loadData(); } }}
                  className="absolute top-6 right-6 p-2 text-slate-200 hover:text-red-500 opacity-0 group-hover:opacity-100"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
                <div className="flex items-center space-x-5 mb-8">
                  <div className="p-5 bg-indigo-600 text-white rounded-[1.5rem] shadow-lg">
                    <SafeIcon icon={FiFolder} className="text-xl" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-slate-800 uppercase">{proj.name}</h3>
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">{proj.collections_list?.length || 0} Itens</span>
                  </div>
                </div>
                <div className="grid gap-2">
                  {(proj.collections_list || []).map(cName => (
                    <div key={cName} className="p-4 bg-slate-50 rounded-2xl text-[10px] font-bold text-slate-600 flex justify-between">
                      {cName}
                      <SafeIcon icon={FiBox} className="text-slate-300" />
                    </div>
                  ))}
                  <button className="w-full py-3 mt-2 border-2 border-dashed border-slate-100 rounded-2xl text-[9px] font-black text-slate-300 uppercase hover:border-indigo-200 hover:text-indigo-400 transition-all">
                    <SafeIcon icon={FiPlus} className="inline mr-2" /> Vincular
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6">Repositório Global</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            {collections.map(col => (
              <button 
                key={col.id} 
                onClick={() => setSelectedCol(col)} 
                className={`p-5 rounded-[1.5rem] text-left transition-all border ${col.system ? 'bg-slate-50 border-slate-100 opacity-60' : 'bg-white border-slate-100 hover:border-indigo-500 hover:shadow-lg'}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${col.system ? 'bg-slate-200 text-slate-400' : 'bg-indigo-50 text-indigo-600'}`}>
                  <SafeIcon icon={col.system ? FiBox : FiDatabase} className="text-sm" />
                </div>
                <div className="text-[9px] font-black text-slate-800 uppercase truncate mb-1">{col.name}</div>
                <div className="text-[7px] font-black text-slate-400 uppercase tracking-tighter">{col.type}</div>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProjectDashboard;